"use client";

import { FavoriteButton } from "@faire/retailer-visitor-shared/components/Favorite/FavoriteButton";
import { closeSaveToBoardToastIfActive } from "@faire/retailer-visitor-shared/features/RetailerBoards/Components/SaveToBoardToast";
import { SaveToBoardContext } from "@faire/retailer-visitor-shared/features/RetailerBoards/Context";
import {
  useSaveToBoard,
  useRemoveFromBoard,
  useMoveToBoard,
} from "@faire/retailer-visitor-shared/features/RetailerBoards/hooks/boardItemsHooks";
import { LazySelectBoardModal } from "@faire/retailer-visitor-shared/features/RetailerBoards/modals/LazySelectBoardModal";
import { trackBoardsEvent } from "@faire/retailer-visitor-shared/features/RetailerBoards/utils";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import * as React from "react";
import styled from "styled-components";

interface IProps {
  /** Used to forward styling from styled-component */
  className?: string;
  /** Product token */
  productToken: string | undefined;
  /** Product image used in the toast component */
  productImage: IImage | undefined;
  /** Board tokens the product belongs to  */
  boardTokens: string[] | undefined;
  /** Brand token  */
  brandToken: string | undefined;
  /** Hear icon color prop */
  iconLightVersion?: boolean;
  /** Hear icon size prop */
  iconSize?: string;
  onHeartClicked?: () => void;
  onSaveToBoard?: (boardToken?: string) => void;
  onRemoveFromBoard?: (boardToken?: string) => void;
  /** data-test-id */
  "data-test-id"?: string;
}

export const SaveToBoard: React.FC<IProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const retailerToken = getRetailer()?.token;
  const initialBoardToken = props.boardTokens?.length
    ? props.boardTokens[0]
    : undefined;
  // currently we support a single board per product
  const savedToBoardTokenRef = React.useRef(initialBoardToken);
  const [favorited, setFavorited] = React.useState(
    isNotUndefined(initialBoardToken)
  );
  const [favoriteDisabled, setFavoriteDisabled] = React.useState(false);
  const [selectBoardModalOpen, setSelectBoardModalOpen] = React.useState(false);
  const saveToBoardContext = React.useContext(SaveToBoardContext);
  const { saveToBoard } = useSaveToBoard(() => setSelectBoardModalOpen(true));
  const { removeFromBoard } = useRemoveFromBoard();
  const { moveToBoard } = useMoveToBoard();

  const { productToken = "" } = props;

  React.useEffect(() => {
    return () => closeSaveToBoardToastIfActive();
  }, []);

  const favorite = async () => {
    try {
      setFavorited(true);
      setFavoriteDisabled(true);
      const { boardToken } = await saveToBoard(
        productToken,
        props.brandToken,
        saveToBoardContext.quickSaveBoardToken,
        props.productImage,
        saveToBoardContext.onSaveProductChange
      );
      savedToBoardTokenRef.current = boardToken;
      setFavoriteDisabled(false);
      props.onSaveToBoard?.(boardToken);

      if (saveToBoardContext.trackOrigin) {
        trackBoardsEvent(
          `product/${productToken}/save/${saveToBoardContext.trackOrigin}`,
          retailerToken ?? ""
        );
      }
    } catch {
      setFavorited(false);
      setFavoriteDisabled(false);
    }
  };

  const unfavorite = async () => {
    if (!savedToBoardTokenRef.current) {
      return;
    }
    try {
      setFavorited(false);
      await removeFromBoard(
        productToken,
        savedToBoardTokenRef.current,
        props.brandToken,
        saveToBoardContext.onSaveProductChange
      );
      props.onRemoveFromBoard?.(savedToBoardTokenRef.current);

      trackBoardsEvent(
        `product/${productToken}/remove/${saveToBoardContext.trackOrigin}`,
        retailerToken ?? ""
      );
      savedToBoardTokenRef.current = undefined;
    } catch {
      setFavorited(true);
    }
  };

  const handleFavoriteClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    event.preventDefault();
    props.onHeartClicked?.();

    if (favorited) {
      unfavorite();
    } else {
      favorite();
    }
  };

  const handleSelectBoard = async (boardTokenToUpdate: string) => {
    props.onSaveToBoard?.(boardTokenToUpdate);
    if (!savedToBoardTokenRef.current) {
      return;
    }
    const { boardToken } = await moveToBoard(
      productToken,
      props.brandToken,
      savedToBoardTokenRef.current,
      boardTokenToUpdate,
      props.productImage
    );
    savedToBoardTokenRef.current = boardToken;
  };

  return (
    <Wrapper className={props.className} data-test-id={props["data-test-id"]}>
      <FavoriteButton
        disabled={favoriteDisabled}
        favorited={favorited}
        onClick={handleFavoriteClick}
        light={props.iconLightVersion}
        size={props.iconSize}
        ariaLabel={
          favorited
            ? strictLocalize({
                id: "eIFxM+",
                defaultMessage: "Remove product from board",
                description: {
                  text: "Accessible button text to remove the product from favorite boards",
                },
              })
            : strictLocalize({
                id: "9j2ud7",
                defaultMessage: "Save product to board",
                description: {
                  text: "Accessible button text to save the product to the favorite boards",
                },
              })
        }
      />
      {selectBoardModalOpen ? (
        <LazySelectBoardModal
          onClose={() => setSelectBoardModalOpen(false)}
          initiallySelectedBoardToken={savedToBoardTokenRef.current}
          onSelect={handleSelectBoard}
        />
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
  z-index: 1;
  pointer-events: auto;
`;
